import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useArticleBookmarks = () => {
  const queryClient = useQueryClient();

  const { data: bookmarks, isLoading } = useQuery({
    queryKey: ['article-bookmarks'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('article_bookmarks')
        .select('article_id')
        .eq('user_id', user.id);

      if (error) throw error;
      return data?.map(b => b.article_id) || [];
    },
  });

  const addBookmark = useMutation({
    mutationFn: async (articleId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Must be logged in to bookmark articles');

      const { error } = await supabase
        .from('article_bookmarks')
        .insert({ user_id: user.id, article_id: articleId });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-bookmarks'] });
      toast.success('Article saved to bookmarks');
    },
    onError: () => {
      toast.error('Failed to save article');
    },
  });

  const removeBookmark = useMutation({
    mutationFn: async (articleId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Must be logged in');

      const { error } = await supabase
        .from('article_bookmarks')
        .delete()
        .eq('user_id', user.id)
        .eq('article_id', articleId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-bookmarks'] });
      toast.success('Bookmark removed');
    },
    onError: () => {
      toast.error('Failed to remove bookmark');
    },
  });

  const isBookmarked = (articleId: string) => {
    return bookmarks?.includes(articleId) || false;
  };

  const toggleBookmark = (articleId: string) => {
    if (isBookmarked(articleId)) {
      removeBookmark.mutate(articleId);
    } else {
      addBookmark.mutate(articleId);
    }
  };

  return {
    bookmarks,
    isLoading,
    isBookmarked,
    toggleBookmark,
    addBookmark: addBookmark.mutate,
    removeBookmark: removeBookmark.mutate,
  };
};
